import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from '@/providers/RouterShim';
import useBranchHierachy from '../../../../../data/branch/useBranchHierarchy';
import type { VehicleExplore } from '../../../../../data/vehicles/useVehicleExplore';
import ShallowPanel, {
  ShallowPanelBody,
} from '../../../../../styled-components/components/Panel/ShallowPanel';
import { PortalNamespace } from '../../../../../styled-components/components/Portal';
import { VStack } from '../../../../../styled-components/components/VStack';
import DataTable from '../../../../../styled-components/structures/DataTable';
import useExistingVehiclesColumns from './useExistingVehiclesColumns';
import VehicleTasksModal from './VehicleTasksModal';

const ns = 'salesAndPurchasingVehicles';

interface ExistingVehiclesProps {
  existingVehicles: VehicleExplore[];
  context?: 'order';
  onClose: () => void;
  showOnlyArchived?: boolean;
  onSelectVehicle?: (vehicle: VehicleExplore) => void;
}

const ExistingVehicles = ({
  existingVehicles,
  context,
  onClose,
  showOnlyArchived,
  onSelectVehicle,
}: ExistingVehiclesProps) => {
  const { t } = useTranslation([ns, 'validation', 'common']);
  const { branchId } = useParams();
  const [branchHierachy] = useBranchHierachy();
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleExplore | null>(null);

  // Data is already filtered by API (archived=true/false), no need to filter locally
  // Sort: current branch first, then others by default
  const sortedVehicles = useMemo(() => {
    return [...existingVehicles].sort((a, b) => {
      const aIsCurrentBranch = a.branchId === branchId;
      const bIsCurrentBranch = b.branchId === branchId;

      if (aIsCurrentBranch && !bIsCurrentBranch) return -1;
      if (!aIsCurrentBranch && bIsCurrentBranch) return 1;

      // Sort by lastUpdatedAt descending
      return new Date(b.lastUpdatedAt).getTime() - new Date(a.lastUpdatedAt).getTime();
    });
  }, [existingVehicles, branchId]);

  const title = showOnlyArchived
    ? t('existingVehicles.archived.heading', { ns, count: existingVehicles.length })
    : t('existingVehicles.heading', { ns });

  const columns = useExistingVehiclesColumns({
    branchHierachy,
    context,
    setSelectedVehicle,
    onSelectVehicle,
  });

  return (
    <>
      <ShallowPanel
        id={PortalNamespace.ExistingVehicles}
        title={title}
        onClose={onClose}
        wide
        leftAdjust
        disableAnimation
      >
        <ShallowPanelBody>
          <VStack $spacing={2}>
            <DataTable
              id={`existing-vehicles-${showOnlyArchived ? 'archived' : 'active'}`}
              data={sortedVehicles}
              columns={columns}
              noSearchResultsText={t('existingVehicles.noResults', {
                ns,
                defaultValue: 'Inga fordon hittades',
              })}
              defaultSort={[]}
              hideTableHeader
            />
          </VStack>
        </ShallowPanelBody>
      </ShallowPanel>
      {selectedVehicle && (
        <VehicleTasksModal vehicle={selectedVehicle} onClose={() => setSelectedVehicle(null)} />
      )}
    </>
  );
};

export default ExistingVehicles;
